import React, { useState, useEffect, useMemo } from 'react';
import { IonPage, IonContent, IonModal, IonRefresher, IonRefresherContent } from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useTasksStore, Task } from '../../store/tasksStore';
import { wsClient } from '../../services/websocket';
import Icon from '../../components/Icon';
import { showToast } from '../../components/Toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import { format, isToday, parseISO } from 'date-fns';
import { parseQuickAdd } from '../../utils/nlQuickAdd';

const PRIORITY_POINTS: Record<string, number> = { low: 1, medium: 2, high: 3 };
const PRIORITY_LABELS: Record<string, string> = { low: 'Easy', medium: 'Medium', high: 'Hard' };

const useIsDesktopTasks = () => {
  const [v, setV] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const h = (e: MediaQueryListEvent) => setV(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return v;
};

const initialsOf = (name?: string) =>
  (name || '').trim().split(/\s+/).map(s => s[0]?.toUpperCase() || '').slice(0, 2).join('') || '·';

const TasksPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const isDesktop = useIsDesktopTasks();
  const { user, family } = useAuthStore();
  const { tasks, fetchTasks, createTask, updateTask, updateTaskStatus, deleteTask: removeTask, handleWsMessage } = useTasksStore();
  const [showModal, setShowModal] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [newDueDate, setNewDueDate] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [difficultyFilter, setDifficultyFilter] = useState<'all' | 'low' | 'medium' | 'high'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string | 'all' | 'mine'>('all');
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [actionTask, setActionTask] = useState<Task | null>(null);
  const [tasksView, setTasksView] = useState<'board' | 'list'>('board');
  const [dragTaskId, setDragTaskId] = useState<string | null>(null);

  const members = family?.members || [];

  useEffect(() => { fetchTasks(); }, []);

  // Auto-open the new task modal when navigated with ?create=1 from the dashboard FAB.
  // Strip the flag from the URL so the modal only fires once per navigation.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      setShowModal(true);
      params.delete('create');
      const nextSearch = params.toString();
      history.replace(`${location.pathname}${nextSearch ? '?' + nextSearch : ''}`);
    }
  }, [location.search]);

  useEffect(() => {
    const unsubs = [
      wsClient.on('TASK_CREATED', handleWsMessage),
      wsClient.on('TASK_UPDATED', handleWsMessage),
      wsClient.on('TASK_STATUS_CHANGED', handleWsMessage),
      wsClient.on('TASK_DELETED', handleWsMessage),
    ];
    return () => unsubs.forEach(u => u());
  }, [handleWsMessage]);

  const handleRefresh = async (event: any) => {
    await fetchTasks();
    event.detail.complete();
  };

  const addTask = async () => {
    if (!newTask.trim() || isSubmitting) return;
    setIsSubmitting(true);
    // NL quick-add: pull date/assignee out of the title if they're embedded,
    // unless the user explicitly set those fields in the form (form wins).
    const parsed = parseQuickAdd(newTask);
    const nlAssigneeId = parsed.assignee
      ? members.find((m: any) => m.displayName?.toLowerCase() === parsed.assignee?.toLowerCase())?.id
      : undefined;
    const effectiveAssignee = assignedTo || nlAssigneeId || '';
    const effectiveDueDate = newDueDate || parsed.date || '';
    // Only send assigned_to when the selected member is in the current family
    // list — otherwise the backend rejects with "Assignee must be a member of
    // your family" and the whole create fails.
    const validAssignee = effectiveAssignee && members.some((m: any) => m.id === effectiveAssignee)
      ? effectiveAssignee
      : undefined;
    const payload: any = {
      title: (parsed.title || newTask).trim(),
      priority: newPriority,
    };
    if (validAssignee) payload.assigned_to = validAssignee;
    if (effectiveDueDate) payload.due_date = effectiveDueDate;
    if (parsed.time) payload.due_time = parsed.time;
    try {
      if (editingTask) {
        await updateTask(editingTask.id, payload);
        showToast('Task updated', 'success');
      } else {
        await createTask(payload);
        showToast('Task created', 'success');
      }
      setNewTask(''); setAssignedTo(''); setNewPriority('medium'); setNewDueDate('');
      setEditingTask(null);
      setShowModal(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        (editingTask ? 'Failed to update task' : 'Failed to create task');
      showToast(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditTask = (t: Task) => {
    setEditingTask(t);
    setNewTask(t.title);
    setAssignedTo(t.assigned_to || '');
    setNewPriority(t.priority || 'medium');
    setNewDueDate(t.due_date || '');
    setActionTask(null);
    setShowModal(true);
  };

  const toggleTask = async (id: string) => {
    const t = tasks.find(x => x.id === id);
    if (!t) return;
    try {
      await updateTaskStatus(id, t.status === 'done' ? 'pending' : 'done');
    } catch {
      showToast('Failed to update task', 'error');
    }
  };

  // ─── Buckets used by the kanban ────────────────────────────────────────
  const isTaskToday = (t: Task) => {
    if (!t.due_date) return false;
    try { return isToday(parseISO(t.due_date)); } catch { return false; }
  };
  const isTaskThisWeek = (t: Task) => {
    if (!t.due_date) return false;
    try {
      const d = parseISO(t.due_date);
      const now = new Date();
      const diff = (d.getTime() - now.getTime()) / 86400000;
      return diff >= -1 && diff <= 7;
    } catch { return false; }
  };

  const backlog = tasks.filter(t => t.status !== 'done' && t.status !== 'in_progress' && !isTaskThisWeek(t));
  const thisWeek = tasks.filter(t => t.status !== 'done' && t.status !== 'in_progress' && isTaskThisWeek(t));
  const inProgress = tasks.filter(t => t.status === 'in_progress');
  const done = tasks.filter(t => t.status === 'done');

  // ─── Fair-share: points per member from completed tasks ───────────────
  const memberPoints = useMemo(() => {
    const points: Record<string, number> = {};
    tasks.filter(t => t.status === 'done').forEach(t => {
      const who = t.assigned_to || t.created_by;
      if (who) points[who] = (points[who] || 0) + (PRIORITY_POINTS[t.priority] || 1);
    });
    return points;
  }, [tasks]);
  const totalPoints = Object.values(memberPoints).reduce((a, b) => a + b, 0);
  const shareRows = members.map(m => ({
    id: m.id,
    name: m.displayName,
    colour: m.colour || '#c5512d',
    points: memberPoints[m.id] || 0,
    pct: totalPoints > 0 ? Math.round((memberPoints[m.id] || 0) / totalPoints * 100) : 0,
  })).sort((a, b) => b.points - a.points);

  const todayCount = tasks.filter(t => t.status !== 'done' && isTaskToday(t)).length;
  const weekCount  = tasks.filter(t => t.status !== 'done' && isTaskThisWeek(t)).length;

  // Move a task between kanban columns. "target" maps to the column dropped onto.
  const moveTaskToColumn = async (taskId: string, target: 'backlog' | 'thisWeek' | 'inProgress' | 'done') => {
    const t = tasks.find(x => x.id === taskId);
    if (!t) return;
    try {
      if (target === 'done') {
        await updateTaskStatus(taskId, 'done');
      } else if (target === 'inProgress') {
        await updateTaskStatus(taskId, 'in_progress');
      } else if (target === 'thisWeek') {
        // Move to "this week" by setting status=pending and due_date within the next 7 days.
        const due = new Date();
        due.setDate(due.getDate() + 3);
        await updateTask(taskId, { status: 'pending', due_date: format(due, 'yyyy-MM-dd') } as any);
      } else {
        // Backlog — clear due date and set status to pending.
        await updateTask(taskId, { status: 'pending', due_date: undefined } as any);
      }
    } catch {
      showToast('Failed to move task', 'error');
    }
  };

  const renderTask = (t: Task) => {
    const members_ = members;
    const assignee = members_.find(m => m.id === t.assigned_to);
    const today_ = isTaskToday(t);
    const blocked = t.status === 'blocked';
    const pts = PRIORITY_POINTS[t.priority] || 1;
    return (
      <div
        key={t.id}
        className={`fhv2-task ${t.status === 'done' ? 'is-done' : ''} ${today_ ? 'is-today' : ''} ${blocked ? 'is-blocked' : ''}`}
        onClick={() => toggleTask(t.id)}
        draggable
        onDragStart={(e) => { setDragTaskId(t.id); e.dataTransfer.setData('text/plain', t.id); e.dataTransfer.effectAllowed = 'move'; }}
        onDragEnd={() => setDragTaskId(null)}
        style={{ cursor: 'grab', opacity: dragTaskId === t.id ? 0.5 : 1 }}
      >
        <div className="fhv2-task-title">
          {blocked ? '🚫 ' : ''}{t.title}
          {blocked && t.blocked_reason ? <span style={{ color: 'var(--fhv2-muted)', fontWeight: 400 }}> ({t.blocked_reason})</span> : null}
        </div>
        <div className="fhv2-task-meta">
          {today_ ? (
            <span className="fhv2-task-tag" style={{ background: 'rgba(233,196,106,0.18)', color: 'var(--fhv2-warn)' }}>
              {t.due_time ? `Today · ${String(t.due_time).slice(0, 5)}` : 'Today'}
            </span>
          ) : blocked ? (
            <span className="fhv2-task-tag" style={{ background: 'rgba(197,81,45,0.15)', color: 'var(--fhv2-terra)' }}>Blocked</span>
          ) : (
            <span className="fhv2-points">{pts} pt{pts === 1 ? '' : 's'}</span>
          )}
          <div className="fhv2-task-who">
            {(() => {
              // Rotation: up to 3 owner initials in a small stack — tasks-desktop.pdf
              // shows "Nightly A D" for a shared recurring task.
              const rotation = (t.rotation_user_ids || []).slice(0, 3);
              const rotationMembers = rotation
                .map(id => members.find(m => m.id === id))
                .filter((m): m is NonNullable<typeof m> => Boolean(m));
              if (rotationMembers.length >= 2) {
                return (
                  <div style={{ display: 'flex' }}>
                    {rotationMembers.map((m, i) => (
                      <div key={m.id} style={{ background: m.colour || '#c5512d', marginLeft: i === 0 ? 0 : -4, border: '2px solid var(--fhv2-ink)' }}>{m.displayName[0]?.toUpperCase()}</div>
                    ))}
                  </div>
                );
              }
              if (assignee) return <div style={{ background: assignee.colour || '#c5512d' }}>{initialsOf(assignee.displayName)}</div>;
              return <span style={{ fontSize: 10, fontFamily: 'var(--fhv2-mono)', color: 'var(--fhv2-muted)', letterSpacing: '0.04em' }}>no owner</span>;
            })()}
          </div>
        </div>
      </div>
    );
  };

  // Drop handler factory for each column
  const dropHandlers = (col: 'backlog' | 'thisWeek' | 'inProgress' | 'done') => ({
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const id = e.dataTransfer.getData('text/plain') || dragTaskId;
      if (id) moveTaskToColumn(id, col);
      setDragTaskId(null);
    },
  });

  // ─── Desktop ───────────────────────────────────────────────────────────
  if (isDesktop) {
    // Build PDF-style fair-share sub: "Ayo 41pts · Adesoji 38pts · balanced. Nan 12pts."
    // Top earners listed with pts, then a one-word verdict, then the person carrying least.
    const fairShareSub = (() => {
      if (shareRows.length === 0) return 'No tasks completed yet — finish one to start the tally.';
      const top = shareRows.slice(0, Math.max(shareRows.length - 1, 1));
      const bottom = shareRows.length > 1 ? shareRows[shareRows.length - 1] : null;
      const topSpread = top.length > 1 ? Math.abs(top[0].pct - top[top.length - 1].pct) : 0;
      const verdict = topSpread < 15 ? 'balanced' : 'skewed';
      const topStr = top.map(s => `${s.name.split(' ')[0]} ${s.points}pts`).join(' · ');
      return bottom
        ? `${topStr} · ${verdict}. ${bottom.name.split(' ')[0]} ${bottom.points}pts.`
        : `${topStr} · ${verdict}.`;
    })();

    // Kids' chores with pocket money — child members earn 50p per completed chore
    const childMembers = members.filter((m: any) => m.role === 'child' || m.role === 'kid');
    const childEarnings = childMembers.map((c: any) => {
      const tasksForChild = tasks.filter(t => t.assigned_to === c.id && t.status === 'done');
      const pounds = tasksForChild.length * 0.5;
      const titles = tasksForChild.slice(0, 3).map(t => t.title.toLowerCase()).join(', ');
      return { ...c, pounds, titles: titles || 'No completed chores' };
    }).filter((c: any) => c.pounds > 0);

    // Streaks: repeated completed-task titles per owner (proxy for recurring chores)
    const STREAK_ICONS = ['🧺', '🗑️', '🛏️', '🍳', '🧹', '🚿'];
    const streakRows = (() => {
      const byOwner: Record<string, { title: string; count: number; ownerName: string }> = {};
      tasks.filter(t => t.status === 'done').forEach(t => {
        const key = `${t.assigned_to || ''}|${t.title}`;
        if (!byOwner[key]) byOwner[key] = { title: t.title, count: 0, ownerName: t.assigned_to_name || '' };
        byOwner[key].count += 1;
      });
      return Object.values(byOwner).filter(s => s.count >= 2).sort((a, b) => b.count - a.count).slice(0, 3);
    })();

    return (
      <IonPage>
        <IonContent>
          <div className="fhv2-desktop">
            <div className="fhv2-today-head">
              <div>
                <h1 className="fhv2-today-title">This week <em>· {weekCount} task{weekCount === 1 ? '' : 's'}</em></h1>
                <div className="fhv2-today-sub">{fairShareSub}</div>
              </div>
              <div className="fhv2-today-head-actions">
                <button
                  className={`fhv2-btn ${tasksView === 'board' ? 'is-active' : ''}`}
                  onClick={() => setTasksView('board')}
                >Board</button>
                <button
                  className={`fhv2-btn ${tasksView === 'list' ? 'is-active' : ''}`}
                  onClick={() => setTasksView('list')}
                >List</button>
                <button className="fhv2-btn is-primary" onClick={() => setShowModal(true)}>+ New task</button>
              </div>
            </div>

            <div className="fhv2-fsection">
              {tasksView === 'board' ? (
              <div className="fhv2-kanban">
                <div className="fhv2-col" {...dropHandlers('backlog')}>
                  <div className="fhv2-col-head"><h5>Backlog</h5><span className="n">{backlog.length}</span></div>
                  {backlog.map(renderTask)}
                  {backlog.length === 0 && <div style={{ color: 'var(--fhv2-muted)', fontSize: 12 }}>Empty — nothing parked.</div>}
                </div>

                <div className="fhv2-col" {...dropHandlers('thisWeek')}>
                  <div className="fhv2-col-head"><h5>This week</h5><span className="n">{thisWeek.length}</span></div>
                  {thisWeek.map(renderTask)}
                  {thisWeek.length === 0 && <div style={{ color: 'var(--fhv2-muted)', fontSize: 12 }}>Nothing due this week.</div>}
                </div>

                <div className="fhv2-col" {...dropHandlers('inProgress')}>
                  <div className="fhv2-col-head"><h5>In progress</h5><span className="n">{inProgress.length}</span></div>
                  {inProgress.map(renderTask)}
                  {inProgress.length === 0 && <div style={{ color: 'var(--fhv2-muted)', fontSize: 12 }}>Nothing in flight.</div>}
                </div>

                <div className="fhv2-col" {...dropHandlers('done')}>
                  <div className="fhv2-col-head"><h5>Done · this week</h5><span className="n">{done.length}</span></div>
                  {done.slice(0, 10).map(renderTask)}
                </div>
              </div>
              ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tasks.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--fhv2-muted)', fontSize: 13 }}>
                    No tasks. <button onClick={() => setShowModal(true)} className="fhv2-unset" style={{ color: 'var(--fhv2-terra)', textDecoration: 'underline', cursor: 'pointer' }}>Add the first one</button>.
                  </div>
                ) : (
                  tasks
                    .slice()
                    .sort((a, b) => {
                      // Sort by status (pending first, done last), then by due date
                      const statusOrder = (s: string) => s === 'done' ? 3 : s === 'in_progress' ? 1 : 2;
                      const ds = statusOrder(a.status) - statusOrder(b.status);
                      if (ds !== 0) return ds;
                      return (a.due_date || '9999').localeCompare(b.due_date || '9999');
                    })
                    .map(t => {
                      const assignee = members.find((m: any) => m.id === t.assigned_to);
                      const pts = PRIORITY_POINTS[t.priority] || 1;
                      return (
                        <div
                          key={t.id}
                          onClick={() => toggleTask(t.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)',
                            borderRadius: 8, cursor: 'pointer',
                            opacity: t.status === 'done' ? 0.55 : 1,
                          }}
                        >
                          <div style={{
                            width: 20, height: 20, borderRadius: '50%',
                            border: `2px solid ${t.status === 'done' ? 'var(--fhv2-sage)' : 'var(--fhv2-line-strong)'}`,
                            background: t.status === 'done' ? 'rgba(130,157,104,0.25)' : 'transparent',
                            flexShrink: 0,
                          }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>{t.title}</div>
                            <div style={{ fontSize: 11, color: 'var(--fhv2-muted)', marginTop: 2 }}>
                              {t.due_date ? format(parseISO(t.due_date), 'EEE d MMM') : 'No due date'}
                              {assignee ? ` · ${assignee.displayName}` : ''}
                              {` · ${pts}pt${pts === 1 ? '' : 's'}`}
                            </div>
                          </div>
                          <span className="fhv2-points">{t.status === 'done' ? 'Done' : t.status === 'in_progress' ? 'In progress' : 'Pending'}</span>
                        </div>
                      );
                    })
                )}
              </div>
              )}

              <div className="fhv2-grid-rail">
                <div className="fhv2-card">
                  <div className="fhv2-card-head"><h4>Fair-share · last 30 days</h4><span className="fhv2-more">Points earned</span></div>
                  <div style={{ padding: '8px 4px' }}>
                    {shareRows.length === 0 ? (
                      <div style={{ color: 'var(--fhv2-muted)', fontSize: 13 }}>No completions yet — finish a task to start the tally.</div>
                    ) : shareRows.map(s => (
                      <div key={s.id} className="fhv2-share-row">
                        <div className="fhv2-share-who">
                          <div className="fhv2-avatar" style={{ background: s.colour }}>{initialsOf(s.name)}</div>
                          <span style={{ fontSize: 13 }}>{s.name}</span>
                        </div>
                        <div className="fhv2-share-bar"><div style={{ background: s.colour, width: `${s.pct}%` }} /></div>
                        <div className="fhv2-share-value">{s.points} pts</div>
                      </div>
                    ))}
                    {shareRows.length > 0 && (
                      <div className="fhv2-share-note">
                        ✓ {shareRows[0].pct < 60 ? 'Workload is balanced — no nag needed this week.' : `${shareRows[0].name} is carrying the most — maybe redistribute.`}
                      </div>
                    )}
                  </div>
                </div>

                <div className="fhv2-stack">
                  {childEarnings.length > 0 && (
                    <div className="fhv2-card">
                      <div className="fhv2-card-head"><h4>Kids' chores · pocket money</h4></div>
                      {childEarnings.map(c => (
                        <div className="fhv2-row" key={c.id}>
                          <div className="fhv2-avatar" style={{ background: c.colour || '#c5512d', width: 28, height: 28 }}>
                            {c.displayName[0]?.toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="title">{c.displayName.split(' ')[0]}</div>
                            <div className="sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.titles}</div>
                          </div>
                          <div style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 12, fontWeight: 700, color: 'var(--fhv2-terra)' }}>
                            £{c.pounds.toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="fhv2-card">
                    <div className="fhv2-card-head"><h4>Streaks</h4></div>
                    {streakRows.length === 0 ? (
                      <div style={{ color: 'var(--fhv2-muted)', fontSize: 13 }}>Complete a recurring task to start a streak.</div>
                    ) : streakRows.map((s, i) => (
                      <div className="fhv2-row" key={`${s.ownerName}-${s.title}`}>
                        <span style={{ fontSize: 16 }}>{STREAK_ICONS[i % STREAK_ICONS.length]}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="title">{s.title}</div>
                          <div className="sub">{s.ownerName ? `${s.ownerName.split(' ')[0]} · ` : ''}{s.count} {s.count === 1 ? 'time' : 'times'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {renderNewTaskModal()}

          <ConfirmDialog
            open={deleteConfirm !== null}
            title="Delete task?"
            message="This task will be permanently removed."
            confirmLabel="Delete"
            onConfirm={() => { if (deleteConfirm) { removeTask(deleteConfirm); setDeleteConfirm(null); } }}
            onCancel={() => setDeleteConfirm(null)}
          />
        </IonContent>
      </IonPage>
    );
  }

  // ─── Mobile ───────────────────────────────────────────────────────────
  const matchesFilters = (t: Task) => {
    if (difficultyFilter !== 'all' && t.priority !== difficultyFilter) return false;
    if (assigneeFilter === 'mine' && t.assigned_to !== user?.id) return false;
    if (assigneeFilter !== 'all' && assigneeFilter !== 'mine' && t.assigned_to !== assigneeFilter) return false;
    return true;
  };
  const todayTasks = tasks.filter(t => t.status !== 'done' && isTaskToday(t)).filter(matchesFilters);
  const weekTasks  = tasks.filter(t => t.status !== 'done' && !isTaskToday(t) && isTaskThisWeek(t)).filter(matchesFilters);
  const doneToday  = tasks.filter(t => t.status === 'done' && isTaskToday(t)).filter(matchesFilters);
  // Anything pending that doesn't land in Today or This-week — without
  // this bucket a task with no due date creates fine but never appears
  // in the mobile list.
  const laterTasks = tasks
    .filter(t => t.status !== 'done' && !isTaskToday(t) && !isTaskThisWeek(t))
    .filter(matchesFilters);

  return (
    <IonPage>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="fhv2-fpage" style={{ padding: 'calc(16px + env(safe-area-inset-top, 0px)) 16px 120px' }}>
          <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0 16px' }}>
            <button
              onClick={() => history.goBack()}
              className="fhv2-m-iconbtn fhv2-unset"
              aria-label="Back"
            >
              <Icon name="arrow_back" size={18} color="rgba(255,255,255,0.7)" />
            </button>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#fff' }}>My tasks</div>
            <button
              onClick={() => setShowModal(true)}
              className="fhv2-m-iconbtn fhv2-unset"
              aria-label="Add task"
            >
              <Icon name="add" size={18} color="rgba(255,255,255,0.9)" />
            </button>
          </header>

          <div className="fhv2-mlead">
            {todayCount > 0 ? (
              <>{todayCount} for today,<br/><em>{weekCount} this week.</em></>
            ) : (
              <>Clear today.<br/><em>{weekCount} this week.</em></>
            )}
          </div>

          {/* Difficulty chips */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 10, scrollbarWidth: 'none' }}>
            {([
              { k: 'all', label: 'All' },
              { k: 'low', label: 'Easy' },
              { k: 'medium', label: 'Medium' },
              { k: 'high', label: 'Hard' },
            ] as const).map(o => {
              const active = difficultyFilter === o.k;
              return (
                <button
                  key={o.k}
                  onClick={() => setDifficultyFilter(o.k as any)}
                  style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: 'var(--fhv2-mono)', letterSpacing: '0.5px', textTransform: 'uppercase', background: active ? 'var(--fhv2-terra)' : 'rgba(255,255,255,0.04)', color: active ? '#fff' : 'var(--fhv2-muted)', border: '1px solid ' + (active ? 'var(--fhv2-terra)' : 'var(--fhv2-line)') }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>

          {/* Assignee chips */}
          {members.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 12, scrollbarWidth: 'none' }}>
              <button
                onClick={() => setAssigneeFilter('all')}
                style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: assigneeFilter === 'all' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)', color: '#fff', border: '1px solid var(--fhv2-line)' }}
              >
                Everyone
              </button>
              <button
                onClick={() => setAssigneeFilter('mine')}
                style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: assigneeFilter === 'mine' ? 'rgba(197,81,45,0.18)' : 'rgba(255,255,255,0.02)', color: assigneeFilter === 'mine' ? 'var(--fhv2-terra)' : '#fff', border: '1px solid ' + (assigneeFilter === 'mine' ? 'rgba(197,81,45,0.4)' : 'var(--fhv2-line)') }}
              >
                Mine
              </button>
              {members.map((m: any) => {
                const active = assigneeFilter === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setAssigneeFilter(m.id)}
                    style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 4px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: active ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)', color: '#fff', border: '1px solid ' + (active ? 'rgba(255,255,255,0.2)' : 'var(--fhv2-line)') }}
                  >
                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: m.colour || '#c5512d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
                      {initialsOf(m.displayName)}
                    </span>
                    {m.displayName.split(' ')[0]}
                  </button>
                );
              })}
            </div>
          )}

          {todayTasks.length > 0 && (
            <>
              <div className="fhv2-msection-label is-today">Today</div>
              {todayTasks.map(t => renderMobileTask(t, toggleTask, members, setActionTask))}
            </>
          )}

          {doneToday.length > 0 && doneToday.map(t => renderMobileTaskDone(t, toggleTask, setActionTask))}

          {weekTasks.length > 0 && (
            <>
              <div className="fhv2-msection-label" style={{ marginTop: 16 }}>This week</div>
              {weekTasks.map(t => renderMobileTask(t, toggleTask, members, setActionTask))}
            </>
          )}

          {laterTasks.length > 0 && (
            <>
              <div className="fhv2-msection-label" style={{ marginTop: 16 }}>Later / no due date</div>
              {laterTasks.map(t => renderMobileTask(t, toggleTask, members, setActionTask))}
            </>
          )}

          {tasks.length === 0 && (
            <div className="fhv2-empty">
              <Icon name="check_circle" size={48} color="var(--fhv2-muted)" />
              <h4>No tasks yet</h4>
              <p>Tap + to add one</p>
            </div>
          )}
        </div>

        {renderNewTaskModal()}

        {/* Action sheet — tap a task card to edit / delete */}
        <IonModal isOpen={actionTask !== null} onDidDismiss={() => setActionTask(null)} breakpoints={[0, 1]} initialBreakpoint={1}>
          <div style={{ background: 'var(--fhv2-ink)', padding: 20, minHeight: '100%', color: '#fff', fontFamily: 'var(--fhv2-sans)' }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)', margin: '0 auto 16px' }} />
            <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 22, marginBottom: 4 }}>{actionTask?.title}</div>
            <div style={{ fontSize: 11, color: 'var(--fhv2-muted)', marginBottom: 16, fontFamily: 'var(--fhv2-mono)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              {actionTask?.status === 'done' ? 'Done' : 'Task actions'}
            </div>
            <button
              onClick={() => { if (actionTask) { toggleTask(actionTask.id); setActionTask(null); } }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 14, marginBottom: 8, textAlign: 'left', cursor: 'pointer' }}
            >
              <Icon name={actionTask?.status === 'done' ? 'undo' : 'check'} size={18} color="#fff" />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{actionTask?.status === 'done' ? 'Mark not done' : 'Mark done'}</span>
            </button>
            <button
              onClick={() => actionTask && openEditTask(actionTask)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 14, marginBottom: 8, textAlign: 'left', cursor: 'pointer' }}
            >
              <Icon name="edit" size={18} color="#fff" />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>Edit task</span>
              <Icon name="chevron_right" size={16} color="var(--fhv2-muted)" />
            </button>
            <button
              onClick={() => { if (actionTask) { setDeleteConfirm(actionTask.id); setActionTask(null); } }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(197,81,45,0.08)', border: '1px solid rgba(197,81,45,0.2)', borderRadius: 12, padding: 14, textAlign: 'left', cursor: 'pointer' }}
            >
              <Icon name="delete" size={18} color="var(--fhv2-terra)" />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--fhv2-terra)' }}>Delete task</span>
            </button>
          </div>
        </IonModal>

        <ConfirmDialog
          open={deleteConfirm !== null}
          title="Delete task?"
          message="This task will be permanently removed."
          confirmLabel="Delete"
          onConfirm={() => { if (deleteConfirm) { removeTask(deleteConfirm); setDeleteConfirm(null); } }}
          onCancel={() => setDeleteConfirm(null)}
        />
      </IonContent>
    </IonPage>
  );

  function renderNewTaskModal() {
    return (
      <IonModal isOpen={showModal} onDidDismiss={() => { setShowModal(false); setEditingTask(null); }} initialBreakpoint={0.55} breakpoints={[0, 0.55, 0.85]}>
        <div style={{ padding: '24px', background: 'var(--fh-dark-surface)', fontFamily: 'Manrope, sans-serif' }}>
          <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20, color: '#fff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{editingTask ? 'Edit task' : 'New task'}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--fh-dark-text-secondary)', display: 'block', marginBottom: 6 }}>Task</label>
              <input
                type="text"
                value={newTask}
                onChange={e => setNewTask(e.target.value)}
                placeholder={'e.g. "take out bins sun 6pm @dad"'}
                autoFocus
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, background: 'var(--fh-dark-bg)', color: 'var(--fh-dark-text)', border: '2px solid var(--fh-dark-border)', fontSize: 14, outline: 'none' }}
                onKeyDown={e => e.key === 'Enter' && addTask()}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--fh-dark-text-secondary)', display: 'block', marginBottom: 6 }}>Difficulty</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['low', 'medium', 'high'] as const).map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setNewPriority(p)}
                    style={{
                      flex: 1, padding: '10px 8px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                      background: newPriority === p ? 'rgba(197,81,45,0.18)' : 'rgba(255,255,255,0.04)',
                      border: newPriority === p ? '1.5px solid var(--fhv2-terra)' : '1.5px solid transparent',
                      color: newPriority === p ? '#fff' : 'rgba(255,255,255,0.45)',
                      cursor: 'pointer',
                    }}
                  >
                    {PRIORITY_LABELS[p]} · {PRIORITY_POINTS[p]}pt{PRIORITY_POINTS[p] > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
            </div>
            {members.length > 0 && (
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--fh-dark-text-secondary)', display: 'block', marginBottom: 6 }}>Assign to</label>
                <select
                  value={assignedTo}
                  onChange={e => setAssignedTo(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 10, background: 'var(--fh-dark-bg)', color: 'var(--fh-dark-text)', border: '2px solid var(--fh-dark-border)', fontSize: 14, outline: 'none', fontFamily: 'Manrope, sans-serif' }}
                >
                  <option value="">Anyone</option>
                  {members.map((m: any) => (
                    <option key={m.id} value={m.id}>{m.displayName}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--fh-dark-text-secondary)', display: 'block', marginBottom: 6 }}>Due date (optional)</label>
              <input
                type="date"
                value={newDueDate}
                onChange={e => setNewDueDate(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, background: 'var(--fh-dark-bg)', color: 'var(--fh-dark-text)', border: '2px solid var(--fh-dark-border)', fontSize: 14, outline: 'none', fontFamily: 'Manrope, sans-serif' }}
              />
            </div>
            <button
              onClick={addTask}
              disabled={!newTask.trim() || isSubmitting}
              style={{
                width: '100%', padding: '14px', borderRadius: 12, fontWeight: 700, color: '#fff', fontSize: 14, border: 'none',
                background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                opacity: (!newTask.trim() || isSubmitting) ? 0.5 : 1,
                cursor: (!newTask.trim() || isSubmitting) ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? 'Saving...' : editingTask ? 'Save changes' : 'Add task'}
            </button>
          </div>
        </div>
      </IonModal>
    );
  }
};

// Mobile task row — task with checkbox, today highlight, points chip.
function renderMobileTask(t: Task, onToggle: (id: string) => void, members: any[], onAction?: (t: Task) => void) {
  const assignee = members.find(m => m.id === t.assigned_to);
  const today_ = (() => {
    try { return t.due_date ? isToday(parseISO(t.due_date)) : false; } catch { return false; }
  })();
  const pts = PRIORITY_POINTS[t.priority] || 1;
  const sub = t.due_time
    ? `${String(t.due_time).slice(0, 5)}${t.due_date ? ` · ${format(parseISO(t.due_date), 'EEE')}` : ''}`
    : t.due_date
      ? format(parseISO(t.due_date), 'EEE d MMM')
      : assignee ? assignee.displayName : '';
  const diffLabel = t.priority === 'high' ? 'Hard' : t.priority === 'low' ? 'Easy' : 'Medium';
  const diffBg = t.priority === 'high' ? 'rgba(197,81,45,0.2)' : t.priority === 'low' ? 'rgba(130,157,104,0.2)' : 'rgba(233,196,106,0.2)';
  const diffColor = t.priority === 'high' ? 'var(--fhv2-terra)' : t.priority === 'low' ? 'var(--fhv2-sage)' : 'var(--fhv2-warn)';
  return (
    <div key={t.id} className={`fhv2-task-card-m ${today_ ? 'is-today' : ''}`} onClick={() => onAction?.(t)}>
      <button className="fhv2-m-checkbox fhv2-unset" onClick={e => { e.stopPropagation(); onToggle(t.id); }} aria-label="Toggle task" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fhv2-task-card-title">{t.title}</div>
        {sub && <div className="fhv2-task-card-sub">{sub}</div>}
        <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 9.5, letterSpacing: '1px', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 4, background: diffBg, color: diffColor }}>
            {diffLabel}
          </span>
          {assignee && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: 'var(--fhv2-muted)' }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', background: assignee.colour || '#c5512d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#fff' }}>
                {(assignee.displayName || '').trim().split(/\s+/).map((s: string) => s[0]?.toUpperCase() || '').slice(0, 2).join('')}
              </span>
              {assignee.displayName?.split(' ')[0]}
            </span>
          )}
        </div>
      </div>
      <span className="fhv2-points">{pts} pt{pts === 1 ? '' : 's'}</span>
    </div>
  );
}

function renderMobileTaskDone(t: Task, onToggle: (id: string) => void, onAction?: (t: Task) => void) {
  const pts = PRIORITY_POINTS[t.priority] || 1;
  return (
    <div key={t.id} className="fhv2-task-card-m is-done" onClick={() => onAction?.(t)}>
      <button className="fhv2-m-checkbox is-checked fhv2-unset" onClick={e => { e.stopPropagation(); onToggle(t.id); }} aria-label="Reopen task">
        <Icon name="check" size={14} color="#fff" />
      </button>
      <div style={{ flex: 1 }}>
        <div className="fhv2-task-card-title">{t.title}</div>
        <div className="fhv2-task-card-sub">Done</div>
      </div>
      <span className="fhv2-points">+{pts}</span>
    </div>
  );
}

export default TasksPage;
